import { useToast } from '@repo/design-system/index'
import { QIcon_ai_speaking_partner, QIcon_bookmark, QIcon_calendar_filled, QIcon_share } from '@repo/icons/index'
import { ActivityComponentType } from '@stackflow/react'
import { QueryClient } from '@tanstack/react-query'
import { useContext, useEffect, useState } from 'react'
import { useEventTracking } from '../../../helpers/gtmHelper'
import apis from '../../../modules/apis'
import { enterConversationAi } from '../../../modules/apis/student/conversation'
import { axiosGet } from '../../../modules/axiosHelper'
import { useCurrentUser } from '../../../modules/context/UserContext'
import { APP_SCREEN_NAME } from '../../../modules/hooks/common/appRouterVars'
import useAppRouter from '../../../modules/hooks/common/useAppRouter'
import useIsWebview from '../../../modules/hooks/common/useIsWebview'
import { useOnclickHeartPacket } from '../../../modules/hooks/common/useOnclickHeartPacket'
import useReturnFullUrl from '../../../modules/hooks/webview/common/useReturnFullUrl'
import { n_ai_speaking, n_material } from '../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../modules/i18n/useTranslation'
import urls from '../../../modules/urls'
import { COLORS } from '../../../modules/vars'
import {
  MaterialSubTab,
  TextContext,
  TextProvider,
  fetchMaterial,
} from '../../../pages/[lang]/portal/materials/detail/[materialId]'
import { useMaterialsStack } from '../../../pages/[lang]/webview/materials'
import MaterialBodyComponent from '../../new-portal/materials/MaterialBodyComponent'
import MaterialMobileBottomTab from '../../new-portal/materials/MaterialMobileBottomTab'
import WebviewStackContainer from '../common/WebviewStackContainer'

interface IMaterialDetail {
  params: {
    materialId: string
    lessonId: any
  }
}

export const ConversationLessonStartButton = ({ onStartConversation, materialId = null }) => {
  const { t } = useTranslation()
  const { sendClickEvent } = useEventTracking()
  const router = useAppRouter()
  const getFullUrl = useReturnFullUrl()

  // Book Lesson
  const handleRouterClick = () => {
    sendClickEvent({ button_name: 'book_lessons' })
    router.push(urls.student.newPortal.lessons.schedule, {
      appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
      appSendData: {
        url: getFullUrl(`${urls.webview.lessons.schedule}?materialId=${materialId}`),
      },
    })
  }

  return (
    <div className="pointer-events-none sticky z-[50] flex w-full justify-center" style={{ bottom: 88 }}>
      <div className="pointer-events-auto relative rounded-[40px] py-[12px] px-[20px] bg-white flex flex-row shadow-lg mb-[16px] select-none">
        <button
          className="z-[10] Body2-14Md flex flex-row align-center"
          onClick={() => onStartConversation(materialId)}
        >
          <QIcon_ai_speaking_partner className="h-[24px] w-[24px] mr-[4px]" color={COLORS.PURPLE500} />
          {t(n_ai_speaking.material.btn_start)}
        </button>
        <div className="bg-gray-200 w-[1px] h-full mx-[12px]" />
        <button className="z-[10] Body2-14Md flex flex-row align-center" onClick={() => handleRouterClick()}>
          <QIcon_calendar_filled className="h-[24px] w-[24px] mr-[4px]" color={COLORS.PURPLE500} />
          {t(n_ai_speaking.material.btn_book)}
        </button>
      </div>
    </div>
  )
}

const MaterialsDetailActivity: ActivityComponentType = ({ params }: IMaterialDetail) => {
  const { useMaterialsFlow } = useMaterialsStack()
  const { pop } = useMaterialsFlow()
  const { showKorean } = useContext(TextContext)
  const { toastSuccess } = useToast()
  const { t } = useTranslation()
  const isWebview = useIsWebview()
  const [activeTabIdx, setActiveTabIdx] = useState<number>(0)
  const [materialData, setMaterialData] = useState(null)
  const [bookmarked, setBookmarked] = useState(false)
  const router = useAppRouter()
  const { currentUser } = useCurrentUser()
  const [isBottomSheetActive, setIsBottomSheetActive] = useState(false)

  const getMaterailData = async () => {
    const queryClient = new QueryClient()

    const [_, json] = await Promise.all([
      queryClient.prefetchQuery(['material', params.materialId], () => fetchMaterial(params.materialId)),
      axiosGet(apis.course.detail({ id: params.materialId })),
    ])

    if (json.success) {
      setMaterialData(json)
      setBookmarked(json.course.is_mine)
    }
  }

  useEffect(() => {
    getMaterailData()
  }, [])

  useEffect(() => {
    if (!materialData) return
    if (!currentUser) return
    setIsConversationAvailable(!materialData.course.is_speaking && !materialData.course.is_writing && !!currentUser)
  }, [materialData, currentUser])

  const onClickHeartPacket = useOnclickHeartPacket({
    isList: false,
    onAdded: () => {
      setBookmarked(true)
      toastSuccess(t(n_material.toast_book_mark))
    },
    onDeleted: () => {
      setBookmarked(false)
      toastSuccess(t(n_material.toast_delete_book_mark))
    },
  })

  const handleShare = (materialData) => {
    if (!materialData) return

    const url = `https://www.ringleplus.com/ko/student/landing/contents/material/${params.materialId}`

    const title = !showKorean
      ? materialData.course.title_en
        ? materialData.course.title_en
        : materialData.course.title
      : materialData.course.title_kr
      ? materialData.course.title_kr
      : materialData.course.title

    router.share(title, url)
  }

  // AI Tutor
  const [isConversationAvailable, setIsConversationAvailable] = useState<boolean | { [x: string]: boolean }>(false)
  const [loading, setLoading] = useState(false)
  const getFullUrl = useReturnFullUrl()

  const onStartConversation = async (courseId) => {
    if (loading) return
    setLoading(true)
    await enterConversationAi({ courseId }, router, getFullUrl)
    setLoading(false)
  }

  // Scroll and Show Top/Bottom Nav
  const [showNav, setShowNav] = useState(true)

  return (
    <TextProvider>
      <WebviewStackContainer
        showBookButton={!isWebview || !isConversationAvailable}
        bookButtonBottomPixed="88px"
        onBack={pop}
        appendRight={() => (
          <div className="flex flex-row">
            <div onClick={(e) => onClickHeartPacket(e, materialData.course)} className="m-[4px] p-[4px]">
              <QIcon_bookmark color={bookmarked ? COLORS.RED400 : COLORS.GRAY300} width={24} height={24} />
            </div>
            <div onClick={(e) => handleShare(materialData)} className="m-[4px] p-[4px]">
              <QIcon_share width={24} height={24} />
            </div>
          </div>
        )}
      >
        <div className="relative min-h-screen">
          <div className="sticky top-[0px] z-10">
            {showNav && <MaterialSubTab activeTabIdx={activeTabIdx} setActiveTabIdx={setActiveTabIdx} />}
          </div>
          {materialData && (
            <div className="relative mt-[20px] min-h-screen">
              <MaterialBodyComponent
                activeTabIdx={activeTabIdx}
                materialData={materialData}
                materialId={params.materialId}
                setShowNav={setShowNav}
              />
            </div>
          )}
        </div>
        {isWebview && isConversationAvailable && !isBottomSheetActive && showNav && (
          <ConversationLessonStartButton onStartConversation={onStartConversation} materialId={params?.materialId} />
        )}
      </WebviewStackContainer>
      {materialData && (
        <MaterialMobileBottomTab
          course={materialData?.course}
          setIsBottomSheetActive={setIsBottomSheetActive}
          isConversation={isConversationAvailable}
          showNav={showNav}
        />
      )}
    </TextProvider>
  )
}

export default MaterialsDetailActivity
